import { RACE_STATES } from "./constants/race";

export class ApiClient {
  constructor(baseURL = "/api") {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  async request(url, options = {}) {
    const isFormData = options.body instanceof FormData;

    const headers = isFormData
      ? { Accept: "application/json" }
      : { ...this.defaultHeaders, ...options.headers };

    const res = await fetch(`${this.baseURL}/${url}`, {
      ...options,
      headers,
    });

    let data = null;
    try {
      data = await res.json();
    } catch {}

    if (!res.ok) {
      throw new Error(data?.error || res.statusText || `HTTP ${res.status}`);
    }

    return data;
  }


  get(url) {
    return this.request(url, { method: "GET" });
  }

  post(url, body) {
    return this.request(url, { method: "POST", body: JSON.stringify(body) });
  }

  patch(url, body) {
    return this.request(url, { method: "PATCH", body: JSON.stringify(body) });
  }

  delete(url) {
    return this.request(url, { method: "DELETE" });
  }


  events = {
    create: (event) => this.post("events", { event }),
    update: (id) => this.get(`events/${id}/edit`),
    getOrganiser: () => this.get("events/new"),
    uploadCSV: (id, csvData) => {
      return this.request(`events/${id}/parse_csv`, {
        method: "POST",
        body: csvData,
      });
    },
    startNow: (id, startTime) => this.patch(
      `events/${id}/start_now`, 
      {event: { start_time: startTime }}
    ),
  };

  bibs = {
    create: (id, bibGroups, bibRules) => {
      const payload = { bib_groups: bibGroups };

      if (bibRules && Object.keys(bibRules).length > 0) {
        payload.bib_rules = bibRules;
      }

      return this.post(`events/${id}/create_bibs`, { bibs: payload });
    },
    getCategories: () => this.get(`events/tempbib`),
    skipCustomSetup: (id) => this.post(`events/${id}/skip_custom_bib_setup`),
  }


  prizes = {
    getGroups: (eventId) => this.get(`events/${eventId}/prize_groups`),
    loadOld: (eventId) => this.get(`events/${eventId}/load_groups`),
    updateTeams: (eventId, teamsPrizes) =>
      this.patch(`events/${eventId}/`, { teams_for_prizes: teamsPrizes }),
    uploadGroup: (eventId, name, howMany) =>
      this.post(`events/${eventId}/prize_groups`, {
        name,
        categories: [],
        how_many: Number(howMany),
      }),
    deleteGroup: (eventId, pgId) =>
      this.delete(`events/${eventId}/prize_groups/${pgId}`),
    uploadCategories: (eventId, pgId, categories) =>
      this.post(`events/${eventId}/prize_groups/${pgId}/set_cats`, {
        cat_ids: categories,
      }),
    updateGroupName: (eventId, pgId, name) =>
      this.patch(`events/${eventId}/prize_groups/${pgId}`, {
        prize_group: { name },
      }),
  };

  stillRacing = {
    getRacers: (eventId) => this.get(`events/${eventId}/still_racing`),
    uploadFinish: (eventId, raceNumber, raceStatus, finTime) =>
      this.post(`events/${eventId}/add_finish`, {
        race_number: raceNumber,
        race_status: raceStatus,
        fin_time: finTime,
      }),
    resetRacer: (eventId, raceNumber) =>
      this.post(`events/${eventId}/add_finish`, {
        race_number: raceNumber,
        race_status: String(RACE_STATES.RACING),
        fin_time: null,
      }),
  };

  printLabels = {
    download: async (eventId, format, labelType) => {
      const res = await fetch(`${this.baseURL}/events/${eventId}/print_labels`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ format: format, label_type: labelType }),
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => null);
        throw new Error(errorText || `HTTP ${res.status}`);
      }
      return { blob: await res.blob(), headers: res.headers };
    },
  };

  entrants = {
    getLocation: (eventId, entrantId) => this.get(`events/${eventId}/entrants/${entrantId}/location`),
    getDetails: (eventId, entrantId) => this.get(`events/${eventId}/entrants/${entrantId}`)
  };
  
  eventControls = {
    get: (id) => this.get(`events/${id}/controls`),
    updateControls: (id, controls) =>
      this.patch(`events/${id}/update_controls`, { controls }),
  };

  tracking = {
    getLocations: (eventId) => this.get(`events/${eventId}/tracking`)
  };
}

export const apiClient = new ApiClient();